import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    DEFAULT_BRAND,
    DEFAULT_CAMPAIGNS,
    DEFAULT_CLIENTS,
    DEFAULT_COMANDAS,
    DEFAULT_MESSAGE_LOG,
    DEFAULT_NOTES_BY_PRO,
    DEFAULT_PRODUCTS,
    DEFAULT_PROFESSIONALS,
    DEFAULT_SERVICES,
    DEFAULT_TEMPLATES,
    DEFAULT_PAYABLES,
    THEMES,
    seedAppointments,
} from "../data/seed";
import { api, auth } from "../lib/api";

// API mirror: chama a API para persistir mutação local; se falhar, silencia
// (o cache local segue como verdade). Só dispara se o usuário estiver logado
// via API (tem token). Retorna uma Promise que SEMPRE resolve (nunca rejeita) —
// callers que queiram exibir um spinner podem dar `await` sem try/catch.
// Retorna null quando não há nada a aguardar (sem token, sem promise).
const mirror = (fn) => {
    if (!auth.getToken()) return null;
    try {
        const p = fn();
        if (p && typeof p.then === "function") {
            return p.then(() => undefined, () => undefined);
        }
        return null;
    } catch (_) {
        return null;
    }
};

// Converte "date" ("YYYY-MM-DD") + "time" ("HH:MM") do backend num timestamp
// numérico ancorado à MEIA-NOITE LOCAL — igual ao formato que todo o resto do
// app espera em appointment.date (Agenda.js soma/compara isso como número, ver
// overlaps() em lib/appointments.js). `new Date("YYYY-MM-DD")` faz parsing como
// UTC (spec ES) — em qualquer fuso a oeste de UTC (todo o Brasil, ex.: UTC-3)
// isso cai no dia ANTERIOR. Por isso um agendamento de hoje sincronizado do
// backend "sumia" da Agenda: ele existia no Mongo, mas era filtrado pro dia
// errado no cliente. Sempre construir o Date a partir dos componentes
// separados (ano, mês, dia, hora, minuto) evita esse parsing em UTC.
const parseLocalDateTime = (dateStr, timeStr) => {
    const [y, m, d] = String(dateStr || "").split("-").map(Number);
    const [hh, mm] = String(timeStr || "00:00").split(":").map(Number);
    if (!y || !m || !d) return Date.now();
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0).getTime();
};

/**
 * SalonContext keeps all reactive state for the multi-tenant SaaS in a single
 * provider. Persistence happens in localStorage for the prototype only.
 */
const SalonContext = createContext(null);

const STORAGE_KEY = "gereBeauty.state.v9";
const REG_KEY = "gereBeauty.registrations.v1";
const CRM_THEME_KEY = "kinng.theme";

// ─── CRM UI Themes (Aparência do Painel) ─────────────────────────────────
// São independentes dos temas do BrandStudio (que afetam landing pública).
// Válidos:
//   vintage        — clássico dourado (padrão de todos os tenants)
//   fresh-green    — sidebar verde, corpo claro
//   midnight       — sidebar preta, corpo branco (minimal)
//   claude-light   — sidebar bege, letras pretas (editorial)
export const CRM_THEMES = [
    {
        id: "vintage",
        cssClass: "theme-vintage-crm",
        name: "Vintage Club",
        description: "Clássico escuro com dourado",
        preview: {
            body: "#0c0a09", card: "#1c1917", sidebar: "#1c1917",
            sidebarText: "#a8a29e", accent: "#d97706", text: "#fafaf9",
        },
    },
    {
        id: "fresh-green",
        cssClass: "theme-fresh-green",
        name: "Fresh Green",
        description: "Sidebar verde, corpo claro",
        preview: {
            body: "#f8f9f8", card: "#f0f4f0", sidebar: "#1a2e1a",
            sidebarText: "#a5d6a7", accent: "#2d7a3a", text: "#1a2e1a",
        },
    },
    {
        id: "midnight",
        cssClass: "theme-midnight",
        name: "Midnight Minimal",
        description: "Sidebar preta, corpo branco",
        preview: {
            body: "#fafafa", card: "#f5f5f5", sidebar: "#1a1a1a",
            sidebarText: "#888888", accent: "#1a1a1a", text: "#1a1a1a",
        },
    },
    {
        id: "claude-light",
        cssClass: "theme-claude-light",
        name: "Claude Light",
        description: "Sidebar bege, letras pretas",
        preview: {
            body: "#f9f9f7", card: "#f0efe9", sidebar: "#f0efe9",
            sidebarText: "#6b6b68", accent: "#1a1a18", text: "#1a1a18",
        },
    },
];

const VALID_CRM_THEMES = CRM_THEMES.map((t) => t.id);

/** Aplica a classe do tema no <body>, na <html> (para overrides globais) e persiste em localStorage. */
export const applyCrmTheme = (themeId) => {
    if (typeof document === "undefined") return "vintage";
    const safe = VALID_CRM_THEMES.includes(themeId) ? themeId : "vintage";
    const body = document.body;
    const html = document.documentElement;

    // 1) Body: adiciona classe do tema (removendo qualquer anterior)
    body.className = body.className
        .split(/\s+/)
        .filter((c) => !/^theme-(vintage-crm|fresh-green|midnight|claude-light)$/.test(c))
        .join(" ")
        .trim();
    const cssClass = CRM_THEMES.find((t) => t.id === safe).cssClass;
    body.classList.add(cssClass);

    // 2) HTML: data-attribute (para o CSS ler no primeiro frame, antes do body) + classe
    //    'crm-light-theme' que ativa os overrides de utilities Tailwind claras.
    html.setAttribute("data-crm-theme", safe);
    if (safe === "vintage") html.classList.remove("crm-light-theme");
    else html.classList.add("crm-light-theme");

    // 3) Limpa inline styles do bootstrap ao trocar de tema (o CSS bundle já
    //    tomou controle e as variáveis --crm-* estão em vigor).
    html.style.backgroundColor = "";
    html.style.color = "";
    body.style.backgroundColor = "";

    // 4) Persistência local (fallback ao reload)
    try { localStorage.setItem(CRM_THEME_KEY, safe); } catch (_) { /* ignore */ }
    return safe;
};

// Super-admin credentials removidas — backend é a única autoridade.
// (Antes: ADMIN_CREDENTIALS hard-coded permitia bypass local.)


const DEFAULT_REGISTRATIONS = [
    {
        id: "reg-demo-1",
        kind: "PJ",
        document: "12.345.678/0001-99",
        ownerName: "João Silva",
        email: "joao@vintageclub.com.br",
        phone: "(11) 98765-4321",
        shopName: "Vintage Club Barbearia",
        shopId: "vintageclub",
        plan: "Imperial",
        status: "active",
        createdAt: Date.now() - 1000 * 60 * 60 * 24 * 12,
        mrr: 297,
    },
    {
        id: "reg-demo-2",
        kind: "PF",
        document: "123.456.789-00",
        ownerName: "Mariana Costa",
        email: "mari@organic-lab.com",
        phone: "(21) 99876-5432",
        shopName: "Organic Hair Lab",
        shopId: "organiclab",
        plan: "Royal",
        status: "active",
        createdAt: Date.now() - 1000 * 60 * 60 * 24 * 5,
        mrr: 197,
    },
    {
        id: "reg-demo-3",
        kind: "PJ",
        document: "98.765.432/0001-11",
        ownerName: "Rafael Pinheiro",
        email: "raf@cybercut.io",
        phone: "(31) 91234-5678",
        shopName: "Neon Cyber Cut",
        shopId: "cybercut",
        plan: "Imperial",
        status: "trial",
        createdAt: Date.now() - 1000 * 60 * 60 * 24 * 2,
        mrr: 0,
    },
];

// Escolhe os horários do backend só quando é um objeto realmente preenchido —
// um `{}` vazio é "truthy" e antes sobrescrevia a grade local recém-salva.
const pickSalonHours = (remoteBrand, fallback) => {
    const remote = remoteBrand && remoteBrand.salonHours;
    if (remote && typeof remote === "object" && Object.keys(remote).length > 0) return remote;
    return fallback;
};

const loadInitial = () => {
    // Sessão real (token de tenant já emitido — login ou self-signup): nunca usa os
    // dados demo da vitrine como fallback. Um tenant de verdade deve começar zerado
    // e esperar o syncTenantData() trazer o estado real do backend (mesmo vazio).
    // Sem token (visitante anônimo vendo o produto antes de logar), mantém o
    // dataset de demonstração de sempre.
    const hasSession = Boolean(auth.getToken());
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const regRaw = localStorage.getItem(REG_KEY);
        const regs = regRaw ? JSON.parse(regRaw) : DEFAULT_REGISTRATIONS;
        if (raw) {
            const parsed = JSON.parse(raw);
            const professionals = parsed.professionals || (hasSession ? [] : DEFAULT_PROFESSIONALS);
            const rawClients = parsed.clients || (hasSession ? [] : DEFAULT_CLIENTS);
            // Backfill missing createdAt so the Identidade panel always shows a date.
            const clients = rawClients.map((c) => (
                c.createdAt ? c : { ...c, createdAt: Date.now() - 90 * 86400000 }
            ));
            const services = parsed.services || (hasSession ? [] : DEFAULT_SERVICES);
            return {
                brand: { ...DEFAULT_BRAND, ...(parsed.brand || {}) },
                comandas: parsed.comandas || DEFAULT_COMANDAS,
                services,
                products: parsed.products || (hasSession ? [] : DEFAULT_PRODUCTS),
                professionals,
                clients,
                notesByPro: parsed.notesByPro || DEFAULT_NOTES_BY_PRO,
                appointments: parsed.appointments || (hasSession ? [] : seedAppointments(professionals, clients, services)),
                campaigns: parsed.campaigns || DEFAULT_CAMPAIGNS,
                templates: parsed.templates || DEFAULT_TEMPLATES,
                messageLog: parsed.messageLog || DEFAULT_MESSAGE_LOG,
                payables: parsed.payables || DEFAULT_PAYABLES,
                registrations: regs,
                loggedIn: false,
            };
        }
    } catch (e) {
        console.warn("Could not load state", e);
    }
    return {
        brand: DEFAULT_BRAND,
        comandas: DEFAULT_COMANDAS,
        services: hasSession ? [] : DEFAULT_SERVICES,
        products: hasSession ? [] : DEFAULT_PRODUCTS,
        professionals: hasSession ? [] : DEFAULT_PROFESSIONALS,
        clients: hasSession ? [] : DEFAULT_CLIENTS,
        notesByPro: DEFAULT_NOTES_BY_PRO,
        appointments: hasSession ? [] : seedAppointments(DEFAULT_PROFESSIONALS, DEFAULT_CLIENTS, DEFAULT_SERVICES),
        campaigns: DEFAULT_CAMPAIGNS,
        templates: DEFAULT_TEMPLATES,
        messageLog: DEFAULT_MESSAGE_LOG,
        payables: DEFAULT_PAYABLES,
        registrations: DEFAULT_REGISTRATIONS,
        loggedIn: false,
    };
};

export const SalonProvider = ({ children }) => {
    const initial = loadInitial();
    const [brand, setBrand] = useState(initial.brand);
    const [comandas, setComandas] = useState(initial.comandas);
    const [services, setServices] = useState(initial.services);
    const [products, setProducts] = useState(initial.products);
    const [professionals, setProfessionals] = useState(initial.professionals);
    const [clients, setClients] = useState(initial.clients);
    const [notesByPro, setNotesByPro] = useState(initial.notesByPro);
    const [appointments, setAppointments] = useState(initial.appointments);
    const [campaigns, setCampaigns] = useState(initial.campaigns);
    const [templates, setTemplates] = useState(initial.templates);
    const [messageLog, setMessageLog] = useState(initial.messageLog);
    const [payables, setPayables] = useState(initial.payables || DEFAULT_PAYABLES);
    const [pendingWizardSeed, setPendingWizardSeed] = useState(null);
    const [registrations, setRegistrations] = useState(initial.registrations);
    const [loggedIn, setLoggedIn] = useState(false);
    const [activeModule, setActiveModule] = useState("login");
    const [needsBootstrapSync, setNeedsBootstrapSync] = useState(false);
    const [adminUnlocked, setAdminUnlocked] = useState(false);
    const [superAdminLoggedIn, setSuperAdminLoggedIn] = useState(false);

    // Persist relevant slices
    useEffect(() => {
        const payload = JSON.stringify({ brand, comandas, services, products, professionals, clients, notesByPro, appointments, campaigns, templates, messageLog, payables });
        localStorage.setItem(STORAGE_KEY, payload);
    }, [brand, comandas, services, products, professionals, clients, notesByPro, appointments, campaigns, templates, messageLog, payables]);

    useEffect(() => {
        localStorage.setItem(REG_KEY, JSON.stringify(registrations));
    }, [registrations]);

    const theme = useMemo(() => THEMES[brand.themeId] || THEMES.vintage, [brand.themeId]);

    // Apply CSS variables dynamically when theme/primary color change
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty("--salon-primary", brand.primaryColor || theme.primary);
        root.style.setProperty("--salon-heading-font", `'${theme.headingFont}', serif`);
        root.style.setProperty("--salon-body-font", `'${theme.bodyFont}', sans-serif`);
    }, [brand.primaryColor, theme]);

    // === CRM UI Theme (independente de BrandStudio) ===
    // Aplica imediatamente no mount usando localStorage para evitar flash de tema errado.
    useEffect(() => {
        const cached = (() => {
            try { return localStorage.getItem(CRM_THEME_KEY); } catch (_) { return null; }
        })();
        applyCrmTheme(cached || "vintage");
    }, []);

    // Reflete mudanças em brand.crmTheme (após login/sync com backend).
    useEffect(() => {
        if (brand.crmTheme) applyCrmTheme(brand.crmTheme);
    }, [brand.crmTheme]);

    // Hard gate de cobrança: o api.js dispara este evento em qualquer 402 do
    // CRM (bloqueio a meio de uma sessão já aberta). A detecção principal é
    // via brand.paymentBlocked vindo de /tenant/settings.access_blocked.
    useEffect(() => {
        const onBlocked = () => setBrand((b) => (b.paymentBlocked ? b : { ...b, paymentBlocked: true }));
        window.addEventListener("kinng:payment-required", onBlocked);
        return () => window.removeEventListener("kinng:payment-required", onBlocked);
    }, []);

    // Bootstrap: se já existe token JWT no localStorage (login prévio ou self-signup),
    // hidrata a sessão automaticamente e loga o tenant.
    useEffect(() => {
        if (loggedIn) return;
        if (!auth.getToken()) return;
        (async () => {
            try {
                const tenant = await api.get("/tenant/settings").catch(() => null);
                if (tenant && tenant.id) {
                    setBrand((b) => ({
                        ...b,
                        shopName: tenant.shop_name || b.shopName,
                        shopId: tenant.shop_id || b.shopId,
                        plan: tenant.plan || b.plan,
                        status: tenant.status || b.status,
                        trial_ends_at: tenant.trial_ends_at || null,
                        paymentBlocked: tenant.access_blocked === true,
                        paymentStatus: tenant.payment_status || "ok",
                        planPriceMonthly: tenant.plan_price_monthly || 0,
                        planName: tenant.plan_name || b.planName,
                        onboarding_completed: tenant.onboarding_completed === true,
                        onboardingComplete: tenant.onboarding_completed === true,
                        tenant_id: tenant.id,
                        crmTheme: (tenant.brand && tenant.brand.theme) || b.crmTheme || "vintage",
                        salonAddress: (tenant.brand && tenant.brand.salonAddress) || b.salonAddress,
                        salonHours: pickSalonHours(tenant.brand, b.salonHours),
                        salonHoursConfigured:
                            (tenant.brand && tenant.brand.salonHoursConfigured === true) || b.salonHoursConfigured === true,
                        welcomeMessage: (tenant.brand && tenant.brand.welcomeMessage) || b.welcomeMessage || "",
                        logoUrl: (tenant.brand && tenant.brand.logoUrl) || b.logoUrl || "",
                    }));
                    setLoggedIn(true);
                    setActiveModule("home");
                    setNeedsBootstrapSync(true);
                }
            } catch (_) { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sincroniza dados completos (clients/services/professionals/etc.) após
    // token bootstrap — evita estado stale ao voltar de reload com sessão válida.
    useEffect(() => {
        if (!needsBootstrapSync) return;
        setNeedsBootstrapSync(false);
        syncTenantData().catch(() => { /* silencioso */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsBootstrapSync]);

    const updateBrand = useCallback((patch) => {
        setBrand((b) => ({ ...b, ...patch }));
    }, []);

    const setTheme = useCallback(
        (themeId) => {
            const t = THEMES[themeId];
            if (!t) return;
            setBrand((b) => ({ ...b, themeId, primaryColor: t.primary }));
        },
        []
    );

    /** Aplica um tema de UI do CRM (independente do BrandStudio) e persiste no backend. */
    const setCrmTheme = useCallback(async (themeId) => {
        const safe = VALID_CRM_THEMES.includes(themeId) ? themeId : "vintage";
        applyCrmTheme(safe);
        setBrand((b) => ({ ...b, crmTheme: safe }));
        // Persistência real no backend: propaga erro para o chamador exibir toast.
        await api.patch("/tenant/settings", { brand: { theme: safe } });
    }, []);

    // Comanda ops
    const createComanda = useCallback((client, professionalId = "pro1", clientId = null) => {
        const newC = {
            id: `c${Date.now()}`,
            client,
            clientId,
            professional: professionalId,
            items: [],
            createdAt: Date.now(),
            status: "open",
            readyToPay: false,
        };
        setComandas((arr) => [newC, ...arr]);
        return newC.id;
    }, []);

    const acknowledgeComanda = useCallback((comandaId) => {
        setComandas((arr) =>
            arr.map((c) => (c.id === comandaId && c.readyToPay ? { ...c, readyToPay: false } : c))
        );
    }, []);

    const addItemToComanda = useCallback((comandaId, item) => {
        setComandas((arr) =>
            arr.map((c) => {
                if (c.id !== comandaId) return c;
                // Snapshot da comissão no momento da venda:
                // 1) Se item.commission for número, prioriza (override do item)
                // 2) Senão, usa a comissão base do profissional da comanda
                const pro = professionals.find((p) => p.id === c.professional);
                const proBaseRate = pro ? Math.round(pro.commissionRate * 100) : 40;
                const itemHasOverride =
                    item.commission !== null &&
                    item.commission !== undefined &&
                    item.commission !== "" &&
                    !Number.isNaN(Number(item.commission));
                const appliedRate = itemHasOverride ? Number(item.commission) : proBaseRate;
                const appliedSource = itemHasOverride ? "item" : "professional";

                const existing = c.items.find((i) => i.id === item.id);
                if (existing) {
                    return {
                        ...c,
                        items: c.items.map((i) =>
                            i.id === item.id ? { ...i, qty: i.qty + 1 } : i
                        ),
                    };
                }
                return {
                    ...c,
                    items: [
                        ...c.items,
                        {
                            ...item,
                            qty: 1,
                            commissionRate: appliedRate, // % aplicada (snapshot)
                            commissionSource: appliedSource, // "item" | "professional"
                            soldAt: Date.now(),
                        },
                    ],
                };
            })
        );
    }, [professionals]);

    const removeItemFromComanda = useCallback((comandaId, itemId) => {
        setComandas((arr) =>
            arr.map((c) =>
                c.id === comandaId
                    ? { ...c, items: c.items.filter((i) => i.id !== itemId) }
                    : c
            )
        );
    }, []);

    const closeComanda = useCallback((comandaId) => {
        setComandas((arr) => arr.filter((c) => c.id !== comandaId));
    }, []);

    // === Catálogo (serviços + produtos) CRUD ===
    const upsertService = useCallback((service) => {
        setServices((arr) => {
            const exists = arr.find((s) => s.id === service.id);
            if (exists) return arr.map((s) => (s.id === service.id ? { ...s, ...service } : s));
            return [...arr, { ...service, id: `s${Date.now()}` }];
        });
        return mirror(() => {
            if (service.id && /^[0-9a-f]{8}-/.test(service.id)) {
                return api.patch(`/tenant/services/${service.id}`, {
                    name: service.name, price: service.price,
                    duration_min: service.duration_min ?? service.durationMin,
                    category: service.category, active: service.active,
                });
            }
            return api.post("/tenant/services", {
                name: service.name, price: service.price || 0,
                duration_min: service.duration_min ?? service.durationMin ?? 30,
                category: service.category || "Serviço",
                active: service.active !== false,
            });
        }) || Promise.resolve();
    }, []);

    const deleteService = useCallback((id) => {
        setServices((arr) => arr.filter((s) => s.id !== id));
        mirror(() => (/^[0-9a-f]{8}-/.test(id) ? api.del(`/tenant/services/${id}`) : null));
    }, []);

    const toggleService = useCallback((id) => {
        setServices((arr) =>
            arr.map((s) => (s.id === id ? { ...s, active: !s.active } : s))
        );
        mirror(() => {
            if (/^[0-9a-f]{8}-/.test(id)) {
                const s = services.find((x) => x.id === id);
                return api.patch(`/tenant/services/${id}`, { active: !(s?.active) });
            }
            return null;
        });
    }, [services]);

    const upsertProduct = useCallback((product) => {
        setProducts((arr) => {
            const exists = arr.find((p) => p.id === product.id);
            if (exists) return arr.map((p) => (p.id === product.id ? { ...p, ...product } : p));
            return [...arr, { ...product, id: `p${Date.now()}` }];
        });
    }, []);

    const deleteProduct = useCallback((id) => {
        setProducts((arr) => arr.filter((p) => p.id !== id));
    }, []);

    const toggleProduct = useCallback((id) => {
        setProducts((arr) =>
            arr.map((p) => (p.id === id ? { ...p, active: !p.active } : p))
        );
    }, []);

    // === Colaboradores (CRUD) ===
    // opts.onSaved(res) — recebe a resposta da API (POST/PATCH /tenant/professionals),
    // usado p/ confirmar o envio da senha de acesso por WhatsApp (res.access_sent).
    const upsertProfessional = useCallback((pro, opts = {}) => {
        let resultId = pro.id;
        setProfessionals((arr) => {
            const exists = arr.find((p) => p.id === pro.id);
            if (exists) return arr.map((p) => (p.id === pro.id ? { ...p, ...pro } : p));
            const newId = `pro${Date.now()}`;
            resultId = newId;
            return [
                ...arr,
                {
                    ...pro,
                    id: newId,
                    avatar: pro.avatar || null,
                    commissionAccrued: 0,
                    agenda: [],
                    transactions: [],
                    active: true,
                    commissionRate: (pro.commission?.rate ?? 40) / 100,
                },
            ];
        });
        mirror(() => {
            if (pro.id && /^[0-9a-f]{8}-/.test(pro.id)) {
                return api.patch(`/tenant/professionals/${pro.id}`, {
                    name: pro.name, role: pro.role,
                    avatar: pro.avatar, active: pro.active,
                    commission_value: pro.commission?.rate ?? pro.commission_value,
                    email: pro.email || undefined,
                    phone: pro.phone || undefined,
                }).then((res) => { if (res) opts?.onSaved?.(res); return res; });
            }
            return api.post("/tenant/professionals", {
                name: pro.name, role: pro.role || "Colaborador",
                avatar: pro.avatar || null,
                commission_type: "percentage",
                commission_value: pro.commission?.rate ?? 40,
                email: pro.email || undefined,
                phone: pro.phone || undefined,
            }).then((res) => { if (res) opts?.onSaved?.(res); return res; });
        });
        return resultId;
    }, []);

    const deleteProfessional = useCallback((id) => {
        setProfessionals((arr) => arr.filter((p) => p.id !== id));
        mirror(() => (/^[0-9a-f]{8}-/.test(id) ? api.del(`/tenant/professionals/${id}`) : null));
    }, []);

    const toggleProfessional = useCallback((id) => {
        setProfessionals((arr) =>
            arr.map((p) => (p.id === id ? { ...p, active: p.active === false } : p))
        );
        mirror(() => {
            if (/^[0-9a-f]{8}-/.test(id)) {
                const p = professionals.find((x) => x.id === id);
                return api.patch(`/tenant/professionals/${id}`, { active: !(p?.active === false ? false : true) });
            }
            return null;
        });
    }, [professionals]);

    // === Agendamentos (fonte única — event-driven) ===
    const upsertAppointment = useCallback((appt) => {
        const idBefore = appt.id;
        setAppointments((arr) => {
            const id = appt.id || `appt-${Date.now()}`;
            const dateTs = typeof appt.date === "number"
                ? appt.date
                : new Date(appt.date).getTime();
            const duration = appt.duration_min || 30;
            const enriched = {
                ...appt,
                id,
                date: dateTs,
                duration_min: duration,
                estimated_end: dateTs + duration * 60000,
                status: appt.status || "agendado",
                source: appt.source || "manual",
            };
            const exists = arr.find((a) => a.id === id);
            if (exists) return arr.map((a) => (a.id === id ? { ...a, ...enriched } : a));
            return [...arr, enriched];
        });
        // Mirror only if the appointment shape has FK ids that match backend UUIDs.
        mirror(() => {
            const clientOk = appt.clientId && /^[0-9a-f]{8}-/.test(appt.clientId);
            const proOk = appt.professionalId && /^[0-9a-f]{8}-/.test(appt.professionalId);
            if (!clientOk || !proOk) return null;
            const dateStr = new Date(typeof appt.date === "number" ? appt.date : appt.date)
                .toISOString().slice(0, 10);
            const timeStr = appt.time
                || new Date(typeof appt.date === "number" ? appt.date : appt.date).toISOString().slice(11, 16);
            if (idBefore && /^[0-9a-f]{8}-/.test(idBefore)) {
                return api.patch(`/tenant/appointments/${idBefore}`, {
                    client_id: appt.clientId, professional_id: appt.professionalId,
                    service_ids: appt.serviceIds || [],
                    date: dateStr, time: timeStr,
                    duration_min: appt.duration_min ?? 30,
                    total: appt.total ?? 0, notes: appt.notes || "",
                    status: appt.status,
                });
            }
            return api.post("/tenant/appointments", {
                client_id: appt.clientId, professional_id: appt.professionalId,
                service_ids: appt.serviceIds || [],
                date: dateStr, time: timeStr,
                duration_min: appt.duration_min ?? 30,
                total: appt.total ?? 0, notes: appt.notes || "",
                status: appt.status || "scheduled",
            });
        });
        return appt.id || `appt-${Date.now()}`;
    }, []);

    const deleteAppointment = useCallback((apptId) => {
        setAppointments((arr) => arr.filter((a) => a.id !== apptId));
        mirror(() => (/^[0-9a-f]{8}-/.test(apptId) ? api.del(`/tenant/appointments/${apptId}`) : null));
    }, []);

    const startAppointment = useCallback((apptId) => {
        const now = Date.now();
        setAppointments((arr) =>
            arr.map((a) => {
                if (a.id !== apptId) return a;
                const duration = a.duration_min || 30;
                return {
                    ...a,
                    status: "em-andamento",
                    started_at: now,
                    estimated_end: now + duration * 60000,
                };
            })
        );
    }, []);

    const finishAppointment = useCallback((apptId) => {
        const now = Date.now();
        setAppointments((arr) => {
            const updated = arr.map((a) =>
                a.id === apptId ? { ...a, status: "concluido", finished_at: now } : a
            );
            // Sync: mark matching open comanda as ready-to-pay.
            // If no comanda exists for this (client, pro) pair, auto-create one
            // with the appointment's service already inserted — so the PDV
            // always receives the "Pronto para Pagamento" pulse, even for
            // services that were booked via Agenda without a prior walk-in.
            const appt = updated.find((a) => a.id === apptId);
            if (appt) {
                setComandas((cs) => {
                    let matched = false;
                    const next = cs.map((c) => {
                        if (c.status !== "open") return c;
                        const sameClient = c.clientId
                            ? c.clientId === appt.clientId
                            : (c.client || "").toLowerCase() === (appt.clientName || "").toLowerCase();
                        const sameProfessional = c.professional === appt.professionalId;
                        if (sameClient && sameProfessional) {
                            matched = true;
                            return { ...c, readyToPay: true };
                        }
                        return c;
                    });
                    if (matched) return next;
                    // Auto-create comanda from appointment
                    const pro = professionals.find((p) => p.id === appt.professionalId);
                    const svc = services.find((s) => s.id === appt.serviceId);
                    const proBaseRate = pro ? Math.round(pro.commissionRate * 100) : 40;
                    const itemHasOverride =
                        svc &&
                        svc.commission !== null &&
                        svc.commission !== undefined &&
                        svc.commission !== "" &&
                        !Number.isNaN(Number(svc.commission));
                    const appliedRate = itemHasOverride ? Number(svc.commission) : proBaseRate;
                    const appliedSource = itemHasOverride ? "item" : "professional";
                    const newC = {
                        id: `c${Date.now()}`,
                        client: appt.clientName || "Cliente",
                        clientId: appt.clientId || null,
                        professional: appt.professionalId,
                        items: svc
                            ? [{
                                id: svc.id,
                                name: svc.name,
                                price: svc.price,
                                icon: svc.icon,
                                kind: "service",
                                qty: 1,
                                commissionRate: appliedRate,
                                commissionSource: appliedSource,
                                soldAt: now,
                            }]
                            : [],
                        createdAt: now,
                        status: "open",
                        readyToPay: true,
                        sourceAppointmentId: apptId,
                    };
                    return [newC, ...next];
                });
            }
            return updated;
        });
    }, [professionals, services]);

    const cancelAppointment = useCallback((apptId) => {
        setAppointments((arr) =>
            arr.map((a) => (a.id === apptId ? { ...a, status: "cancelado" } : a))
        );
    }, []);

    // Atualiza SÓ o status local (sem espelhar um PATCH genérico) — usado pelos
    // fluxos que já disparam o próprio endpoint dedicado logo em seguida
    // (ex.: confirmar/recusar booking → /tenant/appointments/{id}/confirm|refuse).
    // Evita a corrida entre o PATCH genérico do upsertAppointment e o endpoint
    // dedicado (que só aceita status "awaiting_confirmation").
    const setAppointmentLocalStatus = useCallback((apptId, status) => {
        setAppointments((arr) =>
            arr.map((a) => (a.id === apptId ? { ...a, status } : a))
        );
    }, []);

    // === Marketing ===
    const upsertCampaign = useCallback((campaign) => {
        setCampaigns((arr) => {
            const id = campaign.id || `cmp-${Date.now()}`;
            const exists = arr.find((c) => c.id === id);
            const enriched = {
                ...campaign,
                id,
                createdAt: campaign.createdAt || Date.now(),
                stats: campaign.stats || { sent: 0, converted: 0 },
            };
            if (exists) return arr.map((c) => (c.id === id ? { ...c, ...enriched } : c));
            return [...arr, enriched];
        });
    }, []);

    const deleteCampaign = useCallback((campaignId) => {
        setCampaigns((arr) => arr.filter((c) => c.id !== campaignId));
    }, []);

    const toggleCampaign = useCallback((campaignId) => {
        setCampaigns((arr) => arr.map((c) => (c.id === campaignId ? { ...c, active: !c.active } : c)));
    }, []);

    const upsertTemplate = useCallback((template) => {
        setTemplates((arr) => {
            const id = template.id || `tpl-${Date.now()}`;
            const exists = arr.find((t) => t.id === id);
            const enriched = { ...template, id };
            if (exists) return arr.map((t) => (t.id === id ? { ...t, ...enriched } : t));
            return [...arr, enriched];
        });
    }, []);

    const deleteTemplate = useCallback((templateId) => {
        setTemplates((arr) => arr.filter((t) => t.id !== templateId));
    }, []);

    // === Payables (Contas a Pagar) ===
    const upsertPayable = useCallback((payable) => {
        setPayables((arr) => {
            const id = payable.id || `pay-${Date.now()}`;
            const exists = arr.find((p) => p.id === id);
            const enriched = { ...payable, id, status: payable.status || "open" };
            if (exists) return arr.map((p) => (p.id === id ? { ...p, ...enriched } : p));
            return [...arr, enriched];
        });
    }, []);

    const deletePayable = useCallback((payableId) => {
        setPayables((arr) => arr.filter((p) => p.id !== payableId));
    }, []);

    const markPayablePaid = useCallback((payableId) => {
        setPayables((arr) =>
            arr.map((p) => (p.id === payableId ? { ...p, status: "paid", paidAt: Date.now() } : p))
        );
    }, []);

    const payCommission = useCallback((proId, amount) => {
        setProfessionals((arr) =>
            arr.map((p) => (p.id === proId ? { ...p, commissionAccrued: Math.max(0, (p.commissionAccrued || 0) - amount) } : p))
        );
        setPayables((arr) => [
            ...arr,
            {
                id: `pay-cmp-${Date.now()}`,
                kind: "commission",
                description: `Comissão paga · ${proId}`,
                amount,
                dueDate: Date.now(),
                paidAt: Date.now(),
                status: "paid",
                recurring: false,
                proId,
            },
        ]);
    }, []);


    const logMessage = useCallback((entry) => {
        setMessageLog((arr) => [
            ...arr,
            {
                ...entry,
                id: entry.id || `msg-${Date.now()}`,
                sentAt: entry.sentAt || Date.now(),
                status: entry.status || "delivered",
            },
        ]);
        // Also bump campaign stats
        if (entry.campaignId) {
            setCampaigns((arr) =>
                arr.map((c) =>
                    c.id === entry.campaignId
                        ? { ...c, stats: { sent: (c.stats?.sent || 0) + 1, converted: c.stats?.converted || 0 } }
                        : c
                )
            );
        }
    }, []);

    // === Fase 2: Sync com backend real ===
    const [syncStatus, setSyncStatus] = useState({ state: "idle", at: null }); // idle | online | offline
    const syncTenantData = useCallback(async () => {
        if (!auth.getToken()) { setSyncStatus({ state: "idle", at: null }); return; }
        try {
            const [remoteClients, remotePros, remoteServices, remoteAppts, remoteProducts, remoteTenant] = await Promise.all([
                api.get("/tenant/clients").catch(() => null),
                api.get("/tenant/professionals").catch(() => null),
                api.get("/tenant/services").catch(() => null),
                api.get("/tenant/appointments").catch(() => null),
                api.get("/tenant/products").catch(() => null),
                api.get("/tenant/settings").catch(() => null),
            ]);
            const anyOk = [remoteClients, remotePros, remoteServices, remoteAppts, remoteProducts].some(Array.isArray);
            if (remoteTenant && typeof remoteTenant === "object" && remoteTenant.id) {
                // Merge minimal tenant fields into brand (for banners, gating, etc.)
                setBrand((b) => ({
                    ...b,
                    shopName: remoteTenant.shop_name || b.shopName,
                    shopId: remoteTenant.shop_id || b.shopId,
                    plan: remoteTenant.plan || b.plan,
                    status: remoteTenant.status || b.status,
                    trial_ends_at: remoteTenant.trial_ends_at || null,
                    paymentBlocked: remoteTenant.access_blocked === true,
                    paymentStatus: remoteTenant.payment_status || "ok",
                    planPriceMonthly: remoteTenant.plan_price_monthly || 0,
                    planName: remoteTenant.plan_name || b.planName,
                    onboarding_completed: remoteTenant.onboarding_completed === true,
                    onboardingComplete: remoteTenant.onboarding_completed === true,
                    tenant_id: remoteTenant.id,
                    crmTheme: (remoteTenant.brand && remoteTenant.brand.theme) || b.crmTheme || "vintage",
                    salonAddress: (remoteTenant.brand && remoteTenant.brand.salonAddress) || b.salonAddress,
                    salonHours: pickSalonHours(remoteTenant.brand, b.salonHours),
                    salonHoursConfigured:
                        (remoteTenant.brand && remoteTenant.brand.salonHoursConfigured === true) || b.salonHoursConfigured === true,
                    welcomeMessage: (remoteTenant.brand && remoteTenant.brand.welcomeMessage) || b.welcomeMessage || "",
                    logoUrl: (remoteTenant.brand && remoteTenant.brand.logoUrl) || b.logoUrl || "",
                }));
            }
            // NOTA: uma resposta vazia é tão autoritativa quanto uma cheia — um tenant
            // novo de verdade tem 0 clientes/pros/serviços/produtos no backend, e isso
            // precisa sobrescrever qualquer fallback demo que tenha ficado no estado
            // local (ex.: cache antigo no localStorage de antes desta correção).
            if (Array.isArray(remoteClients)) {
                setClients(remoteClients.map((c) => ({
                    ...c,
                    // Compatibilidade com o SPA: garante campos esperados pelo frontend.
                    createdAt: c.createdAt || (c.created_at ? new Date(c.created_at).getTime() : Date.now()),
                    transactions: c.transactions || [],
                    totalSpent: c.total_spent || c.totalSpent || 0,
                    active: c.active !== false,
                })));
            }
            if (Array.isArray(remotePros)) {
                setProfessionals(remotePros.map((p) => {
                    const pct = p.commission_value ?? p.commissionPct ?? 40;
                    return {
                        ...p,
                        commissionPct: pct,
                        commissionRate: (typeof pct === "number" ? pct : parseFloat(pct) || 40) / 100,
                        commission: p.commission || { rate: pct, mode: "percentage" },
                        createdAt: p.created_at ? new Date(p.created_at).getTime() : Date.now(),
                        transactions: p.transactions || [],
                    };
                }));
            }
            if (Array.isArray(remoteServices)) {
                setServices(remoteServices.map((s) => ({
                    ...s,
                    durationMin: s.duration_min ?? s.durationMin ?? 30,
                })));
            }
            if (Array.isArray(remoteAppts)) {
                setAppointments(remoteAppts.map((a) => {
                    const durationMin = a.duration_min ?? 30;
                    const dateTs = parseLocalDateTime(a.date, a.time);
                    return {
                        ...a,
                        date: dateTs,
                        clientId: a.client_id,
                        professionalId: a.professional_id,
                        serviceIds: a.service_ids || [],
                        durationMin,
                        estimated_end: dateTs + durationMin * 60000,
                    };
                }));
            }
            if (Array.isArray(remoteProducts)) {
                setProducts(remoteProducts.map((p) => ({
                    ...p,
                    stock: p.stock_qty ?? p.stock ?? 0,
                    minStock: p.stock_min ?? p.minStock ?? 0,
                })));
            }
            setSyncStatus({ state: anyOk ? "online" : "offline", at: Date.now() });
        } catch (_) {
            setSyncStatus({ state: "offline", at: Date.now() });
        }
    }, []);

    // === Clientes (do colaborador) CRUD ===
    const upsertClient = useCallback((client) => {
        let resolvedId = null;
        setClients((arr) => {
            const exists = arr.find((c) => c.id === client.id);
            if (exists) {
                resolvedId = client.id;
                return arr.map((c) => (c.id === client.id ? { ...c, ...client } : c));
            }
            // Generate new ID from last 5 digits of phone; fallback to timestamp.
            const digits = (client.phone || "").replace(/\D/g, "");
            let baseId = digits.length >= 5 ? digits.slice(-5) : null;
            let newId = baseId || `cli${Date.now().toString().slice(-7)}`;
            // Collision: append suffix
            if (arr.some((c) => c.id === newId)) {
                let i = 2;
                while (arr.some((c) => c.id === `${newId}-${i}`)) i++;
                newId = `${newId}-${i}`;
            }
            resolvedId = newId;
            return [
                ...arr,
                {
                    transactions: [],
                    totalSpent: 0,
                    balance: 0,
                    preferences: [],
                    paymentStatus: "Em Dia",
                    createdAt: Date.now(),
                    ...client,
                    id: newId,
                },
            ];
        });
        // Mirror para API. Retorna a Promise (sempre resolve) pra quem quiser
        // aguardar o request antes de navegar/mostrar sucesso.
        const apiDone = mirror(() => {
            if (client.id) {
                // Only PATCH if this client id looks like a backend UUID (36 chars w/ dashes)
                if (/^[0-9a-f]{8}-/.test(client.id)) {
                    return api.patch(`/tenant/clients/${client.id}`, {
                        name: client.name, phone: client.phone, email: client.email,
                        notes: client.notes, active: client.active,
                    });
                }
            }
            return api.post("/tenant/clients", {
                name: client.name, phone: client.phone || "",
                email: client.email || "", notes: client.notes || "",
            });
        });
        return apiDone || Promise.resolve(resolvedId);
    }, []);

    const deleteClient = useCallback((id) => {
        setClients((arr) => arr.filter((c) => c.id !== id));
        mirror(() => {
            if (/^[0-9a-f]{8}-/.test(id)) return api.del(`/tenant/clients/${id}`);
            return null;
        });
    }, []);

    const toggleClientActive = useCallback((id) => {
        setClients((arr) => arr.map((c) => (
            c.id === id ? { ...c, active: c.active === false } : c
        )));
        mirror(() => {
            if (/^[0-9a-f]{8}-/.test(id)) return api.patch(`/tenant/clients/${id}/toggle`, {});
            return null;
        });
    }, []);

    // === Notas gerais por colaborador ===
    const addNote = useCallback((proId, note) => {
        setNotesByPro((m) => ({
            ...m,
            [proId]: [{ ...note, id: `n${Date.now()}`, date: Date.now() }, ...(m[proId] || [])],
        }));
    }, []);

    const deleteNote = useCallback((proId, noteId) => {
        setNotesByPro((m) => ({
            ...m,
            [proId]: (m[proId] || []).filter((n) => n.id !== noteId),
        }));
    }, []);

    // Registrations (used by signup form + admin panel)
    const addRegistration = useCallback((reg) => {
        const full = {
            id: `reg-${Date.now()}`,
            createdAt: Date.now(),
            status: "trial",
            plan: "Royal",
            mrr: 0,
            ...reg,
        };
        setRegistrations((arr) => [full, ...arr]);
        return full;
    }, []);

    const updateRegistrationStatus = useCallback((id, status) => {
        setRegistrations((arr) =>
            arr.map((r) => (r.id === id ? { ...r, status } : r))
        );
    }, []);

    const deleteRegistration = useCallback((id) => {
        setRegistrations((arr) => arr.filter((r) => r.id !== id));
    }, []);

    // Super-admin auth — backend é a única autoridade.
    // Marcador de sessão apenas: componentes chamam este método após backend 200 OK.
    const superAdminLogin = useCallback(() => {
        setSuperAdminLoggedIn(true);
        return true;
    }, []);

    const superAdminLogout = useCallback(() => setSuperAdminLoggedIn(false), []);

    const value = {
        brand,
        updateBrand,
        theme,
        setTheme,
        setCrmTheme,
        comandas,
        services,
        products,
        upsertService,
        deleteService,
        toggleService,
        upsertProduct,
        deleteProduct,
        toggleProduct,
        professionals,
        upsertProfessional,
        deleteProfessional,
        toggleProfessional,
        upsertAppointment,
        deleteAppointment,
        startAppointment,
        finishAppointment,
        cancelAppointment,
        setAppointmentLocalStatus,
        appointments,
        campaigns,
        templates,
        messageLog,
        upsertCampaign,
        deleteCampaign,
        toggleCampaign,
        upsertTemplate,
        deleteTemplate,
        logMessage,
        payables,
        upsertPayable,
        deletePayable,
        markPayablePaid,
        payCommission,
        pendingWizardSeed,
        setPendingWizardSeed,
        clients,
        upsertClient,
        deleteClient,
        toggleClientActive,
        syncTenantData,
        syncStatus,
        notesByPro,
        addNote,
        deleteNote,
        registrations,
        addRegistration,
        updateRegistrationStatus,
        deleteRegistration,
        superAdminLoggedIn,
        superAdminLogin,
        superAdminLogout,
        loggedIn,
        setLoggedIn,
        activeModule,
        setActiveModule,
        adminUnlocked,
        setAdminUnlocked,
        createComanda,
        acknowledgeComanda,
        addItemToComanda,
        removeItemFromComanda,
        closeComanda,
    };

    return <SalonContext.Provider value={value}>{children}</SalonContext.Provider>;
};

export const useSalon = () => {
    const ctx = useContext(SalonContext);
    if (!ctx) throw new Error("useSalon must be used within SalonProvider");
    return ctx;
};
